import { DocType } from '@/models/docs';

import { GtFileDoc, GtPageSplit } from './types';

export interface SuggestedSplit {
    /** 1-based, as shown in the inputs. */
    startPage: string;
    endPage: string;
    /** An allowed doc type value, or `''`. */
    docType: string;
    /** An allowed folder code, or `''`. */
    folder: string;
}

/**
 * Page-split rows suggested by the documents the pipeline already made for a
 * file, in start-page order. Docs with no page range (e.g. workbook sheets)
 * can't be a page split and are skipped. A doc type or folder the editor's
 * pickers don't offer comes back blank rather than as an unpickable value.
 */
export function suggestSplitsFromDocs(
    docs: GtFileDoc[],
    docTypeValues: ReadonlySet<string>,
    folderCodes: readonly string[],
): SuggestedSplit[] {
    const folderByUpperCode = new Map(
        folderCodes.map((code) => [code.trim().toUpperCase(), code]),
    );
    return docs
        .flatMap((doc) =>
            typeof doc.startPage === 'number' && typeof doc.endPage === 'number'
                ? [{ doc, startPage: doc.startPage, endPage: doc.endPage }]
                : [],
        )
        .sort((a, b) => a.startPage - b.startPage || a.doc.id - b.doc.id)
        .map(({ doc, startPage, endPage }) => ({
            // The API is 0-based; the inputs show 1-based page numbers.
            startPage: String(startPage + 1),
            endPage: String(endPage + 1),
            docType:
                doc.type !== DocType.UNKNOWN_TYPE && docTypeValues.has(doc.type)
                    ? doc.type
                    : '',
            folder:
                folderByUpperCode.get(doc.folder?.trim().toUpperCase() ?? '') ??
                '',
        }));
}

export interface DraftSplit {
    /** Stable React key for the row (not sent to the server). */
    key: string;
    startPage: string;
    endPage: string;
    docType: string;
    folder: string;
    /** A reviewer's free-text remark; empty when none. */
    remark: string;
    /** A person verified this split; saved as the row's own mark. */
    checked: boolean;
}

let nextKey = 0;
export const makeKey = () => `split-${nextKey++}`;

export function emptyDraft(): DraftSplit {
    return {
        key: makeKey(),
        startPage: '',
        endPage: '',
        docType: '',
        folder: '',
        remark: '',
        checked: false,
    };
}

/** A saved split's type as the editor holds it: the stored unknown-type code means no type was set. */
export const draftDocType = (stored: string): string =>
    stored === DocType.UNKNOWN_TYPE ? '' : stored;

export function seedDrafts(splits: GtPageSplit[]): DraftSplit[] {
    return splits.map((s) => ({
        key: makeKey(),
        // Server data is 0-based; the inputs show 1-based page numbers.
        startPage: String(s.startPage + 1),
        endPage: String(s.endPage + 1),
        docType: draftDocType(s.docType),
        folder: s.folder ?? '',
        remark: s.remark ?? '',
        checked: s.checked,
    }));
}
